import React, { useEffect, useState, useCallback } from "react";
import AlertNotification from "../components/AlertNotification";
import { Auth } from "aws-amplify";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import AppBackService from "../services/appback";
import s from "./Setting.module.scss";

const ChangeEmail = ({ userData }) => {
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [oldEmail, setOldEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [showCEPassword, setShowCEPassword] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const handleAlertOpen = useCallback((message) => {
    setAlertMessage(message);
    setAlertOpen(true);
  }, []);

  useEffect(() => {
    if (userData) {
      setConfirmEmail(!userData?.attributes?.email_verified);
      setNewEmail(userData?.attributes?.email);
      setOldEmail(userData?.attributes?.email);
    }
  }, [userData]);

  const handleChangeEmail = useCallback(
    async (model, { setErrors, setStatus }) => {
      setSending(true);
      try {
        const user = await Auth.currentAuthenticatedUser();
        // re-validate the current password (same -> same is a no-op password change)
        await Auth.changePassword(user, model.password, model.password);
        const lowerEmail = model.new_email?.toLocaleLowerCase();
        if (lowerEmail === oldEmail?.toLocaleLowerCase()) {
          throw new Error("New email cannot be the same as the old email.");
        }
        await Auth.updateUserAttributes(userData, { email: lowerEmail });
        setNewEmail(lowerEmail);
        setConfirmEmail(true);
        handleAlertOpen(`Verification code sent to ${lowerEmail}.`);
      } catch (err) {
        setErrors({ api: err?.message });
        setStatus({});
      } finally {
        setSending(false);
      }
    },
    [oldEmail, userData, handleAlertOpen]
  );

  const handleReverify = useCallback(() => {
    setConfirmEmail(false);
  }, []);

  const handleAuthCodeSubmit = useCallback(
    async (values, { setErrors, resetForm, setStatus }) => {
      setVerifying(true);
      try {
        await Auth.verifyCurrentUserAttributeSubmit("email", values.confirm_auth_code);
        // Cognito email change confirmed — sync the new email into appback's DB.
        // appback user records are keyed by the Cognito username (same id used by
        // every other user-scoped AppBackService call). Best-effort: Cognito is the
        // source of truth for auth, so a sync failure is logged but doesn't block success.
        const userId = userData?.username;
        if (userId) {
          const res = await AppBackService.updateUserEmail(userId, newEmail);
          if (res?.error) {
            console.error("Failed to sync email to appback:", res?.msg || res);
          }
        }
        setOldEmail(newEmail);
        setConfirmEmail(false);
        const status = { message: "Your email address has been changed." };
        handleAlertOpen(status.message);
        setStatus(status);
        resetForm({ status });
      } catch (err) {
        setErrors({ api: err?.message });
        setStatus({});
      } finally {
        setVerifying(false);
      }
    },
    [handleAlertOpen, newEmail, userData]
  );

  return (
    <>
      <div className={s.sectionTitle}>Change Email</div>
      <Formik
        initialValues={{ new_email: "", password: "", confirm_auth_code: "" }}
        validationSchema={yup.object().shape({
          new_email: yup.string("Enter your email").email("Enter a valid email").required("Email is required"),
          password: yup.string("Enter your password").required("Password is required"),
        })}
        onSubmit={(values, formikBag) => {
          handleChangeEmail(values, formikBag);
        }}>
        {({ values, errors, touched, handleChange, setErrors, resetForm, setStatus, status }) => (
          <>
            {errors?.api && <div className={s.errorMsg}>{errors.api}</div>}
            {status?.message && <div className={s.successMsg}>{status.message}</div>}
            {!confirmEmail && (
              <Form>
                <div className={s.fieldGroup}>
                  <div>
                    <div className={s.currentEmailLabel}>Current Email</div>
                    <div className={s.currentEmailValue}>{oldEmail}</div>
                  </div>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    id="newemail"
                    name="new_email"
                    autoComplete="email"
                    placeholder="New Email Address"
                    value={values.new_email}
                    onChange={handleChange}
                    error={touched.new_email && Boolean(errors.new_email)}
                    helperText={touched.new_email && errors.new_email}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    id="password"
                    name="password"
                    placeholder="Password"
                    autoComplete="current-password"
                    type={showCEPassword ? "text" : "password"}
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowCEPassword(!showCEPassword)}
                            onMouseDown={(e) => e.preventDefault()}>
                            {showCEPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <button type="submit" className={s.submitBtn} id="change_email_submit_button" disabled={sending}>
                  {sending ? "Sending…" : "Update Email"}
                </button>
              </Form>
            )}

            {confirmEmail && (
              <>
                <div className={s.fieldGroup}>
                  <div className={s.checkEmailText}>
                    Please check {newEmail.toLocaleLowerCase()} for a verification code.
                  </div>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    id="confirm_auth_code"
                    name="confirm_auth_code"
                    placeholder="Verification Code"
                    autoComplete="one-time-code"
                    value={values.confirm_auth_code || ""}
                    onChange={handleChange}
                    error={touched.confirm_auth_code && Boolean(errors.confirm_auth_code)}
                    helperText={touched.confirm_auth_code && errors.confirm_auth_code}
                  />
                </div>
                <button
                  type="button"
                  className={s.submitBtn}
                  onClick={() => handleAuthCodeSubmit(values, { setErrors, resetForm, setStatus })}
                  id="change_email_verify_submit_button"
                  disabled={verifying}>
                  {verifying ? "Verifying…" : "Verify"}
                </button>
                <div className={s.resendLink} onClick={handleReverify}>
                  Resend Email
                </div>
              </>
            )}
          </>
        )}
      </Formik>
      <AlertNotification message={alertMessage} severity={null} open={alertOpen} setOpen={setAlertOpen} />
    </>
  );
};

export default ChangeEmail;
